import { ListChildComponentProps } from 'react-window';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, makeStyles, Skeleton, Text } from '@fluentui/react-components';
import { format, parseISO } from 'date-fns';
import { MemoEdit } from './MemoEdit';
import { ModalBottomButtonContainer } from '../../modals/Styles';
import { InfoButton } from '../Button';
import { MemoDelete } from './MemoDelete';
import { IP4Note } from './MemoEditor';
import generateClientMutationId from '../../../graphql/schema/generateClientMutationId';
import { Maybe } from '../../../graphql/schema/p4types';

const useStyles = makeStyles({
  textDisplay: {
    paddingRight: '200px', //Make some space for the edit/delete buttons so that excessive length text without newlines doesn't get overlapped by them.
    whiteSpace: 'pre-line',
  },
});

export const MemoDisplay = ({
  index,
  style,
  data,
  setRowHeight,
  path,
}: ListChildComponentProps & { setRowHeight: (index: number, height: number) => void; path?: string }) => {
  const note = data[index] as Maybe<IP4Note>;
  const [modalOpen, setModalOpen] = useState(false);
  const key = useMemo(() => {
    return note?.uuid ?? generateClientMutationId();
  }, [note?.uuid]);
  const styles = useStyles();

  const openEditModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const textRef = useRef<any>({});

  //Initialize row height for the variable size list so it can virtualize appropriately - Important that this effect runs when note?.text changes.
  useEffect(() => {
    if (textRef.current) {
      setRowHeight(index, (textRef.current.clientHeight ?? 0) + 35);
    }
  }, [index, setRowHeight, note?.text]);

  if (!note) return <Skeleton />;

  const date = parseISO(note.createdAt);

  return (
    <div style={{ ...style, borderBottom: '1px solid #D2D2D2', overflowY: 'clip' }}>
      <MemoEdit
        key={key}
        item={note}
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        path={path}
      />
      <div style={{ display: 'flex', flexDirection: 'row', paddingTop: '3px' }}>
        <Text data-testid={'note-date'}>{format(date, 'EEE do MMM yyyy HH:mm')}</Text>
        <Badge data-testid={'note-author-name'} color={'informative'} style={{ marginLeft: '5px' }}>
          {note.author?.name}
        </Badge>
        {note.noteType?.title ? (
          <Badge data-testid={'note-type'} color={'informative'} style={{ marginLeft: '5px' }}>
            {note.noteType?.title}
          </Badge>
        ) : null}
      </div>
      <div data-testid={'note-content'} className={styles.textDisplay} ref={textRef}>
        {note?.text}
      </div>
      <ModalBottomButtonContainer>
        {note.deleted ? null : (
          <InfoButton data-testid={'edit-note'} small={true} onClick={openEditModal}>
            Edit
          </InfoButton>
        )}
        <MemoDelete note={note} />
      </ModalBottomButtonContainer>
    </div>
  );
};
